import { useState, useCallback } from 'react';
import { useApiQuery } from './useApiQuery';

export interface EvalSetItem {
  eval_set_id: string;
  created_at: string;
  eval_count: number;
  latest_eval_created_at: string;
  task_names: string[];
  created_by: string | null;
}

interface EvalSetsResponse {
  items: EvalSetItem[];
  total: number;
  page: number;
  limit: number;
}

interface UseEvalSetsOptions {
  page?: number;
  limit?: number;
  search?: string;
  // When false, skip fetching entirely (e.g. a typeahead with no input yet),
  // so we don't hit /meta/eval-sets for the default listing on every mount.
  enabled?: boolean;
}

interface UseEvalSetsResult {
  evalSets: EvalSetItem[];
  isLoading: boolean;
  hasData: boolean;
  error: Error | null;
  total: number;
  page: number;
  limit: number;
  setPage: (page: number) => void;
  setSearch: (search: string) => void;
  setLimit: (limit: number) => void;
  refetch: () => void;
}

const EMPTY_ITEMS: EvalSetItem[] = [];

export function useEvalSets(
  options: UseEvalSetsOptions = {}
): UseEvalSetsResult {
  const {
    page: initialPage = 1,
    limit: initialLimit = 50,
    search: initialSearch = '',
    enabled = true,
  } = options;
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [search, setSearch] = useState(initialSearch);
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (search.trim()) params.set('search', search.trim());
  const query = useApiQuery<EvalSetsResponse>(
    `/meta/eval-sets?${params}`,
    enabled
  );
  const refresh = query.refetch;
  const refetch = useCallback(() => {
    void refresh();
  }, [refresh]);
  const data = enabled ? query.data : undefined;
  return {
    evalSets: data?.items ?? EMPTY_ITEMS,
    total: data?.total ?? 0,
    hasData: data !== undefined,
    isLoading: enabled && query.isLoading,
    error: enabled ? query.error : null,
    page,
    limit,
    setPage,
    setSearch,
    setLimit,
    refetch,
  };
}
